import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { RELYING_PARTIES_REFERENCE_DATA_TYPES, RelyingPartiesReferenceData } from "@constants/reference-data";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getRelyingPartiesReduxRequest } from "@redux-requests/relying-parties";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { AddRelyingPartyModal } from "./add-relying-party-modal/add-relying-party-modal";
import { RelyingPartiesTableContainer } from "./relying-parties-table-container/relying-parties-table-container";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getRelyingPartiesDataState } from "@redux/selectors/relying-parties-selectors";

export const RelyingPartiesContainer = () => {
    const modalContext = useModalContext();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { relyingParties: relyingPartiesTableState } = useSelector(getTableSettingsState);
    const { 
        relyingParties,
        isLoadingRelyingParties
    } = useSelector(getRelyingPartiesDataState);
    const referenceData = useReferenceDataSelector<RelyingPartiesReferenceData>(RELYING_PARTIES_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadRelyingParties, setShouldLoadRelyingParties] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.relyingParties && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                RELYING_PARTIES_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.RELYING_PARTIES
            );
        }
    }, [referenceData.isLoading.relyingParties, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnCompanies,
        referenceData.documentationLinks
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingRelyingParties && shouldLoadRelyingParties) {
            setShouldLoadRelyingParties(false);
            getRelyingPartiesReduxRequest(dispatch)(referenceData.axnCompanies);
        }
    }, [isReferenceDataPresent, isLoadingRelyingParties, shouldLoadRelyingParties]);

    const renderAddRelyingPartyModal = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddRelyingPartyModal
                onModalClose={onModalClose}
                axnCompanies={referenceData.axnCompanies}
            />
        );
    }, [referenceData.axnCompanies]);

    const isLoading = useMemo(() => {
        return isLoadingRelyingParties || referenceData.isLoading.relyingParties;
    }, [
        referenceData.isLoading.relyingParties,
        isLoadingRelyingParties
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            relyingParties &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadRelyingParties
        );
    }, [isLoading, relyingParties, isReferenceDataPresent, shouldLoadReferenceData, shouldLoadRelyingParties]);

    const allowedRelyingParties = useMemo(() => {
        if (relyingParties) {
            return extractRelyingPartiesForPropertyName(
                UserPermissionTypes.VIEW_RELYING_PARTIES,
                session.user.axnAdminUserRPProperties,
                relyingParties
            );
        }

        return [];
    }, [
        session.user.axnAdminUserRPProperties,
        relyingParties
    ]);

    const headerButton = useMemo(() => {
        if (session.userPermissions.canCreateRelyingParties && isLoaded) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddRelyingParty,
                    renderFunction: renderAddRelyingPartyModal
                });
            };

            if (session.limits.relyingParties > relyingParties.length) {
                return (
                    <ButtonWithIcon
                        id={"add-relying-party-button"}
                        title={resources.pages.relyingParties.headerButton}
                        text={resources.pages.relyingParties.headerButton}
                        onClick={onClick}
                        icon={faPlusCircle}
                        disabled={isLoading || !isLoaded}
                    />
                );
            } else {
                const renderTooltip = (props: any) => {
                    return (
                        <Tooltip {...props}>
                            {resources.pages.relyingParties.headerButtonDisabledText}
                        </Tooltip>
                    );
                };

                return (
                    <OverlayTrigger overlay={renderTooltip} placement={"auto"}>
                        <div>
                            <ButtonWithIcon
                                id={"add-relying-party-button"}
                                title={null}
                                text={resources.pages.relyingParties.headerButton}
                                onClick={() => { }}
                                icon={faPlusCircle}
                                disabled={true}
                            />
                        </div>
                    </OverlayTrigger>
                );
            }
        } else {
            return null;
        }
    }, [
        isLoading,
        isLoaded,
        relyingParties,
        session.userPermissions.canCreateRelyingParties,
        session.limits.relyingParties,
        renderAddRelyingPartyModal
    ]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.relyingParties.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"relying-party-list-page-header"}
                    />
                }
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <RelyingPartiesTableContainer
                        canViewCustomHostNames={session.userPermissions.canViewCustomHostNames}
                        canRestoreDueToLimits={session.limits.relyingParties > relyingParties.length}
                        relyingParties={relyingParties}
                        isPageHeight={true}
                        allowedRelyingParties={allowedRelyingParties}
                        tableState={relyingPartiesTableState}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};